import React, {
    ComponentProps,
    PropsWithChildren,
    ComponentType,
    ElementType,
    ReactNode,
} from 'react';
import {
    Tab as CommonTab,
    TabProps as CommonTabProps,
    SvgIconProps,
    tabClasses,
} from '@mui/material';

import { Counter } from '../Counter';
import { Typography } from '../Typography';


export type TabProps<
    TComponent extends ElementType = 'div',
    TProps = ComponentProps<TComponent>,
> = Pick<CommonTabProps<TComponent, TProps>, 'value'> & {
    variant: 'button' | 'text';
    gray?: boolean;
    active?: boolean;
    icon?: ComponentType<SvgIconProps>;
    component?: TComponent;
    count?: number;
    label: ReactNode;
};

export function Tab<TComponent extends ElementType = 'div', TProps = ComponentProps<TComponent>>({
    variant,
    gray,
    active,
    icon: Icon,
    count,
    label,
    children,
    ...props
}: PropsWithChildren<TabProps<TComponent, TProps>>) {
    return (
        <CommonTab
            data-testid={`tab-${label}`}
            icon={Icon && <Icon />}
            label={(
                <>
                    <Typography
                        ml={Icon && 2}
                        variant='mediumCaption1'
                    >
                        {label}
                    </Typography>
                    {count && (
                        <Counter variant='primary' active={active} gray={gray} count={count} ml={2} />
                    )}
                </>
            )}
            iconPosition='start'
            sx={({ spacing }) => ({
                width: variant === 'button' ? spacing(50) : 'auto',
                minHeight: spacing(8),
                py: 0,
                px: variant === 'button' ? spacing(4) : 0,
                opacity: 1,
                backgroundColor:
                    variant === 'button'
                        ? gray
                            ? 'grayscale.shade10'
                            : 'grayscale.shade0'
                        : 'inherit',
                borderRadius: variant === 'button' ? spacing(25) : 0,
                color: 'grayscale.shade95',

                transition: 'color 150ms ease-out, backgroundColor 150ms ease-out',

                [`&.${tabClasses.root}`]: {
                    alignItems: variant === 'button' ? 'center' : 'flex-start',
                },

                [`&.${tabClasses.selected}`]: {
                    color: variant === 'button' ? 'grayscale.shade0' : 'general.primary50',
                    backgroundColor: variant === 'button' && 'general.primary50',

                    '&:hover': {
                        color: variant === 'text' && 'general.primary60',
                        backgroundColor: variant === 'button' && 'general.primary50',
                    },
                },

                [`& .${tabClasses.iconWrapper}`]: {
                    width: spacing(5),
                    height: spacing(5),
                    mr: 0,
                },

                '&:hover': {
                    color: variant === 'text' && 'grayscale.shade90',
                    backgroundColor:
                        variant === 'button'
                            ? gray
                                ? 'grayscale.shade20'
                                : 'grayscale.shade10'
                            : 'none',
                },
                ...variant === 'button' ? {
                    '&:active': {
                        color: 'grayscale.shade0',
                        backgroundColor: 'general.primary60',
                    },
                } : {},
            })}
            disableRipple
            {...props}
        />
    );
}
